import React from "react";
import { Link } from "gatsby";
import { PageContext } from "gatsby/internal";

const isCurrent = (page: number, currentPage: number) => page === currentPage;

const trimSlash = (url: string) => {
  while (url.endsWith("/")) {
    url = url.substring(0, url.length - 1);
  }

  return url;
};

const getPageUrl = (baseUrl: string, pageNumber: number | null) => {
  if (pageNumber === 1 || !pageNumber) {
    const url = baseUrl + "/";

    return trimSlash(url);
  }

  const url = baseUrl + "/" + pageNumber;

  return trimSlash(url);
};

interface PageLabelProps {
  label: string;
}

const PageLinkActive = ({ label }: PageLabelProps) => {
  return <span className="font-bold text-accent2-300 drop-shadow-neon-lg">{label}</span>;
};

const PageLinkDisabled = ({ label }: PageLabelProps) => {
  return <span className="text-accent2-900">{label}</span>;
};

interface PageLinkProps {
  pageNum: number;
  paginationUrl: string;
  currentPage: number;
}

const PageLink = ({ pageNum, paginationUrl, currentPage }: PageLinkProps) => {
  const labelPageNum = pageNum + 1;

  if (isCurrent(labelPageNum, currentPage)) {
    return <PageLinkActive label={labelPageNum.toString()} />;
  }

  return (
    <Link
      to={getPageUrl(paginationUrl, pageNum === 0 ? null : labelPageNum)}
      className="text-accent2-600 hover:-translate-y-0.5 transition-transform duration-150 ease-in-out"
    >
      {labelPageNum}
    </Link>
  );
};

export interface PaginationProps {
  pageContext: PageContext;
  paginationUrl: string;
}

/**
 * Hide previous page link on the first page.
 *
 * Hide next page link on the last page.
 *
 * Disable link to the current page.
 *
 * @param pageContext
 * @param paginationUrl
 * @returns {JSX.Element}
 * @constructor
 */
const Pagination = ({ pageContext, paginationUrl }: PaginationProps) => {
  const { currentPage, numPages } = pageContext;
  const isFirst = currentPage === 1;
  const isLast = currentPage === numPages;
  const prevPage = currentPage - 1 === 1 ? null : (currentPage - 1);
  const nextPage = (currentPage + 1).toString();

  return (
    <div className="py-10 px-5 flex gap-5 justify-center text-lg">
      {isFirst ? (
        <PageLinkDisabled label={"←"} />
      ) : (
        <Link
          to={getPageUrl(paginationUrl, prevPage)}
          rel="prev"
          className="text-accent2-600 hover:-translate-x-0.5 transition-transform duration-150 ease-in-out"
        >
          ←
        </Link>
      )}
      {Array.from({ length: numPages }, (_, i) => (
        <PageLink
          key={i}
          pageNum={i}
          paginationUrl={paginationUrl}
          currentPage={currentPage}
        />
      ))}
      {isLast ? (
        <PageLinkDisabled label={"→"} />
      ) : (
        <Link
          to={getPageUrl(paginationUrl, nextPage)}
          rel="next"
          className="text-accent2-600 hover:translate-x-0.5 transition-transform duration-150 ease-in-out"
        >
          →
        </Link>
      )}
    </div>
  );
};

export default Pagination;
